import React, { useEffect, useRef, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, Form } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import { createLayoutInstance } from '../../../Models/Layout';
import { LanguageService } from '../../../Services/Axios/Api';

const LayoutBuilder = () => {
    const [layoutInstance] = useState(createLayoutInstance());
    const [currentData, setCurrentData] = useState(layoutInstance.getData());
    const [activeColorPicker, setActiveColorPicker] = useState(null);
    const [activeHeaderPicker, setActiveHeaderPicker] = useState(null);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [fontOptions, setFontOptions] = useState([
        { family: 'Montserrat' },
        { family: 'Roboto' },
        { family: 'Open Sans' },
        { family: 'Lato' },
        { family: 'Poppins' },
        { family: 'Cairo' },
        { family: 'Inter' },
        { family: 'Nunito' },
        { family: 'Source Sans Pro' },
        { family: 'Work Sans' }
    ]);
    const loadedFonts = useRef(new Set());

    useEffect(() => {
        initializeData();
    }, []);

    useEffect(() => {
        (currentData.fonts || []).forEach(item => ensureFontLoaded(item.font));
    }, [currentData.fonts, fontOptions]);

    const initializeData = async () => {
        try {
            const languages = await loadLanguages();
            await loadGoogleFonts();
            await getData(languages);
        } catch (error) {
            console.error(error);
        }
    };

    const loadGoogleFonts = async () => {
        const apiKey = "AIzaSyDbjuj_t6QgHBD66mbOzCS-f4QEG2f_EmM";
        if (!apiKey) {
            // Keep silent fallback to static list to avoid blocking
            return;
        }
        try {
            const response = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}&sort=popularity`);
            const data = await response.json();
            const fonts = data?.items || [];

            // Pick top popular fonts by subset: 20 latin (English) + 10 arabic.
            const englishFonts = fonts
                .filter(item => (item.subsets || []).includes('latin'))
                .slice(0, 20);

            const arabicFonts = fonts
                .filter(item => (item.subsets || []).includes('arabic'))
                .slice(0, 20);

            // Combine while de-duping by family; keep order (english first then arabic).
            const combinedMap = new Map();
            [...englishFonts, ...arabicFonts].forEach(font => {
                if (font?.family && !combinedMap.has(font.family)) {
                    combinedMap.set(font.family, font);
                }
            });
            const combined = Array.from(combinedMap.values()).slice(0, 30);

            if (combined.length) {
                setFontOptions(combined);
            }
        } catch (error) {
            console.error('Error loading google fonts:', error);
            // fallback to existing list
        }
    };

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            const languages = response?.result || [];
            setAvailableLanguages(languages);
            return languages;
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error("Failed to load languages");
            return [];
        }
    };

    const getData = async (languagesList = availableLanguages) => {
        try {
            await layoutInstance.load();
            layoutInstance.syncFontsWithLanguages(languagesList, fontOptions[0]);
            setCurrentData({ ...layoutInstance.getData() });
        } catch (error) {
            console.error(error);
            toast.error("Failed to load layout data");
        }
    };

    const handleButtonShapeChange = (shape) => {
        layoutInstance.setButtonShape(shape);
        setCurrentData({ ...layoutInstance.getData() });
    };

    const handleButtonColorChange = (buttonType, property, color) => {
        const buttons = { ...currentData.buttons };
        buttons[buttonType] = { ...buttons[buttonType], [property]: color.hex };
        layoutInstance.setButtons(buttons);
        setCurrentData({ ...layoutInstance.getData() });
    };

    const handleHeaderColorChange = (level, color) => {
        const headers = { ...currentData.headers, [level]: color.hex };
        layoutInstance.setHeaders(headers);
        setCurrentData({ ...layoutInstance.getData() });
    };

    const handleFontChange = (languageCode, fontFamily) => {
        const fontObj = fontOptions.find(f => f.family === fontFamily) || { family: fontFamily };
        layoutInstance.setFont(languageCode, fontObj);
        ensureFontLoaded(fontObj);
        setCurrentData({ ...layoutInstance.getData() });
    };

    const ensureFontLoaded = (font) => {
        const fontFamily = typeof font === 'string' ? font : font?.family;
        if (!fontFamily || loadedFonts.current.has(fontFamily)) return;
        const linkId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;
        if (document.getElementById(linkId)) {
            loadedFonts.current.add(fontFamily);
            return;
        }
        const link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
        document.head.appendChild(link);
        loadedFonts.current.add(fontFamily);
    };

    const handleSaveChanges = async () => {
        try {
            await layoutInstance.save();
            toast.success("Layout changes saved successfully!");
            getData();
        } catch (error) {
            toast.error("Failed to save changes. Please try again.");
        }
    };

    const renderColorPicker = (show, color, onChange, onClose) => {
        if (!show) return null;
        return (
            <div style={{ position: 'absolute', zIndex: 2 }}>
                <div 
                    style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                    onClick={onClose}
                />
                <SketchPicker color={color} onChange={onChange} />
            </div>
        );
    };

    const renderColorSwatch = (color, onClick) => (
        <div 
            style={{
                width: '30px',
                height: '30px',
                backgroundColor: color,
                border: '1px solid #ccc',
                marginRight: '10px',
                cursor: 'pointer'
            }}
            onClick={onClick}
        />
    );

    const buttonTypes = [
        { 
            name: 'Primary Button', 
            key: 'CTAPrimaryButton',
            hoverKey: 'CTAPrimaryButtonHover'
        },
        { 
            name: 'Secondary Button', 
            key: 'CTASecondaryButton',
            hoverKey: 'CTASecondaryButtonHover'
        }
    ];

    return (
        <div className="container mt-4">
            <h3 className="mb-3">Layout Builder</h3>
            <Accordion defaultActiveKey={['2']} alwaysOpen>
                {/* Buttons Section */}
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Buttons</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-4">
                            <h5>Button Shape</h5>
                            <div className="d-flex gap-2">
                                {['normal', 'rounded', 'oval'].map(shape => (
                                    <Button
                                        key={shape}
                                        variant={currentData.buttons.shape === shape ? 'primary' : 'outline-primary'}
                                        onClick={() => handleButtonShapeChange(shape)}
                                    >
                                        {shape}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div className="row">
                            {buttonTypes.map((buttonType) => (
                                <div key={buttonType.key} className="col-md-6">
                                    <h5>{buttonType.name}</h5>
                                    
                                    {/* Normal State */}
                                    <h6 className="mt-3">Normal State</h6>
                                    {Object.entries(currentData.buttons[buttonType.key]).map(([property, color]) => (
                                        <div key={`${buttonType.key}-${property}`} className="mb-3">
                                            <label className="form-label">{property}</label>
                                            <div className="d-flex align-items-center">
                                                {renderColorSwatch(color, () => 
                                                    setActiveColorPicker({
                                                        type: buttonType.key,
                                                        property,
                                                        show: true
                                                    })
                                                )}
                                                <span>{color}</span>
                                            </div>
                                            {renderColorPicker(
                                                activeColorPicker?.type === buttonType.key && 
                                                activeColorPicker?.property === property && 
                                                activeColorPicker.show,
                                                color,
                                                (color) => handleButtonColorChange(buttonType.key, property, color),
                                                () => setActiveColorPicker(null)
                                            )}
                                        </div>
                                    ))}
                                    
                                    {/* Hover State */}
                                    <h6 className="mt-3">Hover State</h6>
                                    {Object.entries(currentData.buttons[buttonType.hoverKey]).map(([property, color]) => (
                                        <div key={`${buttonType.hoverKey}-${property}`} className="mb-3">
                                            <label className="form-label">{property}</label>
                                            <div className="d-flex align-items-center">
                                                {renderColorSwatch(color, () => 
                                                    setActiveColorPicker({
                                                        type: buttonType.hoverKey,
                                                        property,
                                                        show: true
                                                    })
                                                )}
                                                <span>{color}</span>
                                            </div>
                                            {renderColorPicker(
                                                activeColorPicker?.type === buttonType.hoverKey && 
                                                activeColorPicker?.property === property && 
                                                activeColorPicker.show,
                                                color,
                                                (color) => handleButtonColorChange(buttonType.hoverKey, property, color),
                                                () => setActiveColorPicker(null)
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                {/* Headers Section */}
                {/* <Accordion.Item eventKey="1">
                    <Accordion.Header>Headers</Accordion.Header>
                    <Accordion.Body>
                        <div className="row">
                            {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(level => (
                                <div key={level} className="col-md-4 mb-3">
                                    <label className="form-label">{level.toUpperCase()}</label>
                                    <div className="d-flex align-items-center">
                                        {renderColorSwatch(
                                            currentData.headers[level],
                                            () => setActiveHeaderPicker(level)
                                        )}
                                        <span>{currentData.headers[level]}</span>
                                    </div>
                                    {renderColorPicker(
                                        activeHeaderPicker === level,
                                        currentData.headers[level],
                                        (color) => handleHeaderColorChange(level, color),
                                        () => setActiveHeaderPicker(null)
                                    )}
                                </div>
                            ))}
                        </div>
                    </Accordion.Body>
                </Accordion.Item> */}

                <Accordion.Item eventKey="2">
                    <Accordion.Header>Fonts by Language</Accordion.Header>
                    <Accordion.Body>
                        {availableLanguages.length === 0 ? (
                            <p className="mb-0 text-muted">No languages available.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table align-middle">
                                    <thead>
                                        <tr>
                                            <th scope="col">Language</th>
                                            <th scope="col">Code</th>
                                            <th scope="col">Font</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {availableLanguages.map(lang => {
                                            const fontEntry = currentData.fonts?.find(f => f.language === lang.code);
                                            const currentFont = fontEntry?.font;
                                            const selectedFont = typeof currentFont === 'string'
                                                ? currentFont
                                                : currentFont?.family || fontOptions[0]?.family || '';
                                            return (
                                                <tr key={lang.code}>
                                                    <td>{lang.name || lang.nativeName || lang.code}</td>
                                                    <td>{lang.code}</td>
                                                    <td style={{ maxWidth: '240px' }}>
                                                        <Form.Select
                                                            
                                                            value={selectedFont}
                                                            onChange={(e) => handleFontChange(lang.code, e.target.value)}
                                                        >
                                                            {fontOptions.map(font => (
                                                                <option key={`${lang.code}-${font.family}`} value={font.family}>{font.family}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="SaveChangesContainer d-flex justify-content-end mt-3">
                <ToastContainer />
                <Button variant="primary" onClick={handleSaveChanges}>Save Changes</Button>
            </div>
        </div>
    );
};

export default LayoutBuilder;